import React, { useState } from 'react';
import Modal from '../../../../components/Modal';
import Button from '../../../../components/Button';

type DeactivationModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (reason: string, date: string) => void;
    branchCode?: string;
    title?: string;
    message?: string;
};

export default function DeactivationModal({ isOpen, onClose, onConfirm, branchCode, title = 'Deactivate Branch', message }: DeactivationModalProps) {
    const [reason, setReason] = useState('');
    const [date, setDate] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const newErrors: Record<string, string> = {};

        if (!reason.trim()) {
            newErrors.reason = 'Reason is required';
        }
        if (!date.trim()) {
            newErrors.date = 'Date is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        onConfirm(reason.trim(), date);
        // Reset form
        setReason('');
        setDate('');
        setErrors({});
    };

    const handleClose = () => {
        setReason('');
        setDate('');
        setErrors({});
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={title}
            size="md"
        >
            <form onSubmit={handleSubmit} className="p-6">
                <div className="mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {message ? message : <>Are you sure you want to deactivate branch <strong>{branchCode}</strong>?</>}
                    </p>
                </div>

                <div className="mb-4">
                    <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Reason for Deactivation <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        value={reason}
                        onChange={(e) => {
                            setReason(e.target.value);
                            if (errors.reason) {
                                setErrors(prev => ({ ...prev, reason: '' }));
                            }
                        }}
                        rows={4}
                        placeholder="Enter the reason for deactivation..."
                        className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none ${errors.reason
                            ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
                            : 'border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                            }`}
                    />
                    {errors.reason && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.reason}</p>
                    )}
                </div>

                <div className="mb-6">
                    <label className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                        Deactivation Date <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => {
                            setDate(e.target.value);
                            if (errors.date) {
                                setErrors(prev => ({ ...prev, date: '' }));
                            }
                        }}
                        max={new Date().toISOString().split('T')[0]}
                        className={`w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none ${errors.date
                            ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
                            : 'border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                            }`}
                    />
                    {errors.date && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.date}</p>
                    )}
                </div>

                <div className="flex justify-end gap-3">
                    <Button type="button" variant="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button type="submit">
                        Confirm Deactivation
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

